'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import { toast } from '@/lib/toast';
import {
  getDevItems,
  DevItem,
  DevItemStatus,
  DevItemPriority,
  DevItemType,
} from '@/services/api/devtracker';
import { getUsers, type User } from '@/services/api/users';
import { ApiClientError } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, RefreshCw, Layers } from 'lucide-react';
import { colors, typography } from '@/styles/theme';

interface ItemFilters {
  keyword: string;
  status: DevItemStatus | 'ALL';
  priority: DevItemPriority | 'ALL';
  itemType: DevItemType | 'ALL';
}

const PRIORITY_OPTIONS: DevItemPriority[] = [
  DevItemPriority.P0,
  DevItemPriority.P1,
  DevItemPriority.P2,
  DevItemPriority.P3,
];

const STATUS_OPTIONS: DevItemStatus[] = [
  DevItemStatus.DRAFT,
  DevItemStatus.REVIEWED,
  DevItemStatus.IN_DEVELOPMENT,
  DevItemStatus.IN_TESTING,
  DevItemStatus.USER_TESTING,
  DevItemStatus.DONE,
  DevItemStatus.CANCELLED,
  DevItemStatus.ARCHIVED,
];

const TYPE_OPTIONS: DevItemType[] = [DevItemType.MODULE, DevItemType.FEATURE, DevItemType.BUG];

export default function DevItemListPage() {
  const { t } = useTranslation();
  const { isReady } = useAuthGuard();
  const router = useRouter();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<DevItem[]>([]);
  const [filters, setFilters] = useState<ItemFilters>({
    keyword: '',
    status: 'ALL',
    priority: 'ALL',
    itemType: 'ALL',
  });
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0, totalPages: 0 });
  const [userMap, setUserMap] = useState<Record<string, User>>({});

  const loadUsers = useCallback(async () => {
    try {
      const data = await getUsers({ page: 1, pageSize: 100 });
      const map: Record<string, User> = {};
      data.items.forEach((item) => {
        map[item.id] = item;
      });
      setUserMap(map);
    } catch (error) {
      // 忽略用户加载失败，不阻断页面
    }
  }, []);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getDevItems({
        page: pagination.page,
        pageSize: pagination.pageSize,
        keyword: filters.keyword || undefined,
        status: filters.status === 'ALL' ? undefined : filters.status,
        priority: filters.priority === 'ALL' ? undefined : filters.priority,
        itemType: filters.itemType === 'ALL' ? undefined : filters.itemType,
      });
      setItems(result.items);
      setPagination(result.pagination);
    } catch (error) {
      if (error instanceof ApiClientError) {
        toast.error(error.message);
      } else {
        toast.error(t.devtracker?.toast?.loadFailed);
      }
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.pageSize, t.devtracker?.toast?.loadFailed]);

  useEffect(() => {
    if (!isReady) return;
    loadItems();
    loadUsers();
  }, [isReady, loadItems, loadUsers]);

  const getStatusLabel = (status: DevItemStatus) => {
    return t.devtracker?.status?.[status] || status;
  };

  const getStatusStyle = (status: DevItemStatus) => {
    const colorMap: Record<DevItemStatus, string> = {
      [DevItemStatus.DRAFT]: colors.textTertiary,
      [DevItemStatus.REVIEWED]: colors.info,
      [DevItemStatus.IN_DEVELOPMENT]: colors.primaryHover,
      [DevItemStatus.IN_TESTING]: colors.warning,
      [DevItemStatus.USER_TESTING]: colors.primary,
      [DevItemStatus.DONE]: colors.success,
      [DevItemStatus.CANCELLED]: colors.error,
      [DevItemStatus.ARCHIVED]: colors.textSecondary,
    };
    const color = colorMap[status] ?? colors.textSecondary;
    return {
      borderColor: color,
      color,
      backgroundColor: `${color}1A`,
    };
  };

  const getPriorityLabel = (priority: DevItemPriority) => {
    return t.devtracker?.priority?.[priority] || priority;
  };

  const getTypeLabel = (itemType: DevItemType) => {
    return t.devtracker?.itemType?.[itemType] || itemType;
  };

  if (!isReady) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-gray-400">
        {t.devtracker?.loading}
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      <div className="bg-white border-b" style={{ borderColor: colors.border }}>
        <div className="px-6 h-12 flex items-center justify-between">
          <h1 className={typography.h4}>
            <span className="inline-flex items-center gap-2">
              <Layers className="w-5 h-5 text-blue-600" />
              {t.devtracker?.items?.title}
            </span>
          </h1>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={loadItems} className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              {t.devtracker?.actions?.refresh}
            </Button>
            <Button size="sm" onClick={() => router.push('/devtracker/items/new')} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              {t.devtracker?.actions?.createItem}
            </Button>
          </div>
        </div>
      </div>

      <div className="px-6">
        <p className="text-sm text-gray-500">{t.devtracker?.items?.subtitle}</p>
      </div>

      <div className="px-6">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Input
              placeholder={t.devtracker?.filters?.keyword}
              value={filters.keyword}
              onChange={(event) => setFilters((prev) => ({ ...prev, keyword: event.target.value }))}
            />
            <Select
              value={filters.itemType}
              onValueChange={(value) => setFilters((prev) => ({ ...prev, itemType: value as DevItemType | 'ALL' }))}
            >
              <SelectTrigger className="justify-between">
                <div className="flex w-full items-center">
                  <span className="text-xs text-gray-500">{t.devtracker?.filters?.itemType}</span>
                  <span className="ml-auto text-right text-sm text-gray-900">
                    <SelectValue />
                  </span>
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">{t.devtracker?.filters?.all}</SelectItem>
                {TYPE_OPTIONS.map((itemType) => (
                  <SelectItem key={itemType} value={itemType}>
                    {getTypeLabel(itemType)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.status}
              onValueChange={(value) => setFilters((prev) => ({ ...prev, status: value as DevItemStatus | 'ALL' }))}
            >
              <SelectTrigger className="justify-between">
                <div className="flex w-full items-center">
                  <span className="text-xs text-gray-500">{t.devtracker?.filters?.status}</span>
                  <span className="ml-auto text-right text-sm text-gray-900">
                    <SelectValue />
                  </span>
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">{t.devtracker?.filters?.all}</SelectItem>
                {STATUS_OPTIONS.map((status) => (
                  <SelectItem key={status} value={status}>
                    {getStatusLabel(status)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.priority}
              onValueChange={(value) => setFilters((prev) => ({ ...prev, priority: value as DevItemPriority | 'ALL' }))}
            >
              <SelectTrigger className="justify-between">
                <div className="flex w-full items-center">
                  <span className="text-xs text-gray-500">{t.devtracker?.filters?.priority}</span>
                  <span className="ml-auto text-right text-sm text-gray-900">
                    <SelectValue />
                  </span>
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">{t.devtracker?.filters?.all}</SelectItem>
                {PRIORITY_OPTIONS.map((priority) => (
                  <SelectItem key={priority} value={priority}>
                    {getPriorityLabel(priority)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="px-6">
        <div className="bg-white border border-gray-200 rounded-xl">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-100">
                <TableHead>{t.devtracker?.detail?.labels?.code}</TableHead>
                <TableHead>{t.devtracker?.table?.type}</TableHead>
                <TableHead>{t.devtracker?.table?.priority}</TableHead>
                <TableHead>{t.devtracker?.table?.status}</TableHead>
                <TableHead>{t.devtracker?.table?.owner}</TableHead>
                <TableHead>{t.devtracker?.table?.updatedAt}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow className="border-gray-100">
                  <TableCell colSpan={6} className="text-center text-gray-400">
                    {t.devtracker?.loading}
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow className="border-gray-100">
                  <TableCell colSpan={6} className="text-center text-gray-400">
                    {t.devtracker?.empty?.items}
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.id} className="border-gray-100">
                    <TableCell>
                      <Link
                        href={`/devtracker/items/${item.id}`}
                        className="font-medium text-gray-900 hover:text-blue-600"
                      >
                        {item.code || item.id}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{getTypeLabel(item.itemType)}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{getPriorityLabel(item.priority)}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" style={getStatusStyle(item.status)}>
                        {getStatusLabel(item.status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {userMap[item.ownerId]?.displayName || item.ownerId}
                    </TableCell>
                    <TableCell>
                      {new Date(item.updatedAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
